/**
 * Client-side encryption for auth payloads in localStorage / sessionStorage.
 * Uses AES-256-CBC (see utils/crypto.js). Legacy XOR (`gs:v1:`) and plaintext still decrypt.
 */

import { decrypt as aesDecrypt, encrypt as aesEncrypt } from './crypto';
import { clearAllAppCache, TAB_SESSION_ACTIVE_KEY } from './cacheManager';

const AES_ENC_PREFIX = 'gs:aes:v1:';
const LEGACY_XOR_PREFIX = 'gs:v1:';

export const AUTH_TOKEN_KEY = 'authToken';
export const AUTH_USER_KEY = 'authUser';
export const USER_LEGACY_KEY = 'user';

function getSecretBytes() {
  const env = process.env.REACT_APP_CLIENT_STORAGE_SECRET;
  const keyStr =
    env && String(env).length >= 16
      ? String(env)
      : 'getsettled-client-storage-fallback-min16';
  return new TextEncoder().encode(keyStr);
}

function xorTransform(bytes, keyBytes) {
  const out = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) {
    out[i] = bytes[i] ^ keyBytes[i % keyBytes.length];
  }
  return out;
}

function bytesToBase64(bytes) {
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function encryptForStorage(plain) {
  if (plain == null) return '';
  const str = typeof plain === 'string' ? plain : JSON.stringify(plain);
  return AES_ENC_PREFIX + aesEncrypt(str);
}

export function decryptFromStorage(stored) {
  if (stored == null || stored === '') return null;
  if (typeof stored !== 'string') return null;
  if (stored.startsWith(AES_ENC_PREFIX)) {
    return aesDecrypt(stored.slice(AES_ENC_PREFIX.length));
  }
  if (stored.startsWith(LEGACY_XOR_PREFIX)) {
    try {
      const enc = base64ToBytes(stored.slice(LEGACY_XOR_PREFIX.length));
      const dec = xorTransform(enc, getSecretBytes());
      return new TextDecoder().decode(dec);
    } catch {
      return null;
    }
  }
  return stored;
}

function getDecryptedFrom(storage, key) {
  if (!storage || typeof storage.getItem !== 'function') return null;
  const raw = storage.getItem(key);
  if (raw == null) return null;
  return decryptFromStorage(raw);
}

/** Plain JWT (or legacy plaintext). Prioritizes sessionStorage so tab close clears auth automatically. */
export function getStoredAuthToken() {
  const b = typeof sessionStorage !== 'undefined' ? getDecryptedFrom(sessionStorage, AUTH_TOKEN_KEY) : null;
  if (b && String(b).length) return b;

  // If in localStorage from a previous session, verify if the current tab session is active
  if (typeof localStorage !== 'undefined') {
    const a = getDecryptedFrom(localStorage, AUTH_TOKEN_KEY);
    if (a && String(a).length) {
      // If no active tab session exists, this is a fresh tab after closure — purge and do not reuse
      const hasActiveTabSession =
        typeof sessionStorage !== 'undefined' && sessionStorage.getItem(TAB_SESSION_ACTIVE_KEY);
      if (!hasActiveTabSession) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
        localStorage.removeItem(USER_LEGACY_KEY);
        return null;
      }
      // Migrate to sessionStorage and remove from localStorage
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(AUTH_TOKEN_KEY, localStorage.getItem(AUTH_TOKEN_KEY));
      }
      localStorage.removeItem(AUTH_TOKEN_KEY);
      return a;
    }
  }

  return null;
}

/** Parsed user object from encrypted or legacy storage. Prioritizes sessionStorage. */
export function getStoredAuthUserJson() {
  const parseJsonSafe = (raw) => {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  if (typeof sessionStorage !== 'undefined') {
    const raw =
      getDecryptedFrom(sessionStorage, AUTH_USER_KEY) || getDecryptedFrom(sessionStorage, USER_LEGACY_KEY);
    const parsed = parseJsonSafe(raw);
    if (parsed) return parsed;
  }

  if (typeof localStorage !== 'undefined') {
    const hasActiveTabSession =
      typeof sessionStorage !== 'undefined' && sessionStorage.getItem(TAB_SESSION_ACTIVE_KEY);
    if (!hasActiveTabSession) {
      localStorage.removeItem(AUTH_USER_KEY);
      localStorage.removeItem(USER_LEGACY_KEY);
      return null;
    }
    const raw =
      getDecryptedFrom(localStorage, AUTH_USER_KEY) || getDecryptedFrom(localStorage, USER_LEGACY_KEY);
    const parsed = parseJsonSafe(raw);
    if (parsed && typeof sessionStorage !== 'undefined') {
      const enc = encryptForStorage(JSON.stringify(parsed));
      sessionStorage.setItem(AUTH_USER_KEY, enc);
      sessionStorage.setItem(USER_LEGACY_KEY, enc);
      localStorage.removeItem(AUTH_USER_KEY);
      localStorage.removeItem(USER_LEGACY_KEY);
    }
    return parsed;
  }

  return null;
}

/**
 * Persist auth credentials to sessionStorage so tab close clears auth automatically.
 * Removes credentials from localStorage to avoid persistent cross-session leakage.
 * @param {{ token?: string | null, user?: object | null }} patch
 */
export function persistAuthCredentials(patch) {
  if (!patch || typeof patch !== 'object') return;

  if (Object.prototype.hasOwnProperty.call(patch, 'token')) {
    const { token } = patch;
    if (token) {
      const enc = encryptForStorage(token);
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(AUTH_TOKEN_KEY, enc);
      }
      // Keep localStorage clean so closing tab ends session
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    } else {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    }
  }

  if (Object.prototype.hasOwnProperty.call(patch, 'user')) {
    const { user } = patch;
    if (user != null) {
      const enc = encryptForStorage(JSON.stringify(user));
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(AUTH_USER_KEY, enc);
        sessionStorage.setItem(USER_LEGACY_KEY, enc);
      }
      // Keep localStorage clean so closing tab ends session
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(AUTH_USER_KEY);
        localStorage.removeItem(USER_LEGACY_KEY);
      }
    } else {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(AUTH_USER_KEY);
        sessionStorage.removeItem(USER_LEGACY_KEY);
      }
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(AUTH_USER_KEY);
        localStorage.removeItem(USER_LEGACY_KEY);
      }
    }
  }
}

export function clearAuthCredentials() {
  persistAuthCredentials({ token: null, user: null });
  clearAllAppCache({ preserveLanguage: true });
}

